<template>
  <div class="document-selector-table">
    <div class="pt-4 pb-2">
      <span v-translate>Select the file you want to attach or </span>
      <GekkoButton 
        @click="addFile"
        :text="$gettext('add a new document from your computer.')" 
        variant="link" 
        class="inline p-0 border-0"
      />
    </div>
    <div class="document-selector-table-container is-shadow-box" style="overflow: hidden;">
      <div class="content--inner">
        <overlay-scrollbars :options="scrollBarsOptions">
          <b-table 
            :items="filteredDocumentList"
            :fields="fields"
            class="document-table"
            :class="['gk-table gk-table--responsive gk-table--striped gk-table--headed gk-table--hover']"
            @row-clicked="rowClicked"
            show-empty
            table-busy
            :busy="loading"
            sticky-header
          >
            <template #cell(file_size)="row">
              {{ niceBytes(row.item.file_size, 1000, true) }}
            </template>

            <template #empty="">
              <EmptyState class="emptystate-simple">
                <template v-slot:title>
                  {{ $gettext('We couldn\'t find any document') }}
                </template>
              </EmptyState>
            </template>
            <template #table-busy="">
              <LoadingSpinner min-height />
            </template>
            <template slot="bottom-row" v-if="nextUrl">
              <td :colspan="fields.length" class="table-load-more">
                <GekkoButton 
                  v-if="nextUrl && !fetchingData" 
                  @click="handleScroll()"
                  id="load_more_button" 
                  class="text-info"
                  variant="link" 
                  :text="$gettext('Load more documents')" 
                />
                <LoadingSpinner v-if="fetchingData" />
              </td>
            </template>
          </b-table>
        </overlay-scrollbars>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { niceBytes } from "@/utils/";
import DocumentService from "@/store/services/document.service";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GekkoButton from "@/components/GekkoButton"
import EmptyState from "@/components/ui/EmptyState"

export default {
  name: "DocumentSelector",
  components: {
    LoadingSpinner,
    GekkoButton,
    EmptyState
  },
  props: {
    showFileUploader: {
      type: Boolean,
      default: false
    },
    excludeFiles: {
      type: Array,
      default: () => []
    },
  },
  data () {
    return {
      documentList: [],
      fields: [
        { key: "title", label: this.$gettext('Title'), tdClass: 'tablecol-md' },
        { key: "file_size", label: this.$gettext('Size'),  tdClass: 'tablecol-sm colsize' },
        { key: "description", label: this.$gettext('Description'), tdClass: 'coldesc' }
      ],
      nextUrl: null,
      loading: true,
      fetchingData: false,
      scrollBarsOptions: {
        callbacks: {
          onScrollStop: this.handleScroll
        }
      }
    }
  },
  computed: {
    ...mapGetters({ 
      getCompanyId: 'main/getCompanyId'
    }),
    filteredDocumentList () {
      let document_list = this.documentList
      if (this.excludeFiles && this.excludeFiles.length > 0) {
        document_list = this.documentList.filter(document => !this.excludeFiles.includes(document.id));
      }
      return document_list
    }
  },
  created () {
    window.addEventListener('scroll', this.handleScroll)

    this.getDocuments()
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getDocuments () {
      this.loading = true
      this.fetchingData = true
      DocumentService.list({ company_profile: this.getCompanyId }).then((response) => {
        this.documentList = response.data.results
        this.nextUrl = response.data.next
        this.loading = false
        this.fetchingData = false
      })
    },
    handleScroll () {
      if (!this.fetchingData && this.nextUrl) {
        var loadMoreDocuments = this.isInViewport('load_more_button')
        if (loadMoreDocuments) {
          this.fetchingData = true
          DocumentService.listNextPage(this.nextUrl).then((response) => {
            this.fetchingData = false
            this.nextUrl = response.data.next

            this.documentList = this.documentList.concat(response.data.results)

            if (this.$route.params['documentId']) {
              this.documentList = this.documentList.filter((item) => item.id !== this.$route.params['documentId'])
            }
          })
        }
      }
    },
    isInViewport(element_id) {
      var element = document.getElementById(element_id)
      if (!element) return false
      var rect = element.getBoundingClientRect();
      var html = document.documentElement;
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      );
    },
    rowClicked(item) {
      this.documentList = this.documentList.filter((it) => it.id !== item.id);
      this.$emit("documentSelected", item);
    },
    removeItem(item) {
      this.documentList = this.documentList.filter((it) => it.id !== item.id);
    },
    addFile () {
      if (this.showFileUploader) {
        this.$emit("showFileUploader", false)
      } else {
        this.$router.push({ name: 'AddDocument' })
      }
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
  }
}
</script>

<style lang="scss">
  .document-selector-table-container {
    .os-host {
      max-height: 21rem !important;
      .os-content { padding: 0 !important; }
      .os-scrollbar-vertical { top: 36px !important; }
    }
    .document-table {
      overflow: visible !important;
      thead {
        text-transform: none;
        color: $colorTextBase;
      }
      th.table-b-table-default {
        background: #f8f8f8 !important;
      }
    }
  }
</style>